import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchTicketStatus, claimTicket, clearError } from '../store/ticketsSlice';
import i18n from '../../../i18n';

export const useTicketCollection = () => {
  const dispatch = useDispatch();
  const tickets = useSelector(state => state.tickets);
  const [timeRemaining, setTimeRemaining] = useState(null);

  const ticketsRef = useRef(tickets);
  const lastFetchForTimerRef = useRef(0);

  useEffect(() => {
    ticketsRef.current = tickets;
  }, [tickets]);

  // Статус тикетов загружается один раз после логина в useAuth
  // Если данных еще нет (первая загрузка), загружаем здесь
  useEffect(() => {
    // Загружаем только если данные еще не инициализированы
    if (!tickets.isInitialized && !tickets.isLoading && !tickets.error) {
      dispatch(fetchTicketStatus());
    }
  }, [dispatch, tickets.isInitialized, tickets.isLoading, tickets.error]);

  // Обновляем таймер каждую секунду
  useEffect(() => {
    if (!tickets.nextAvailableTime || tickets.isAvailable) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const nextTime = new Date(ticketsRef.current.nextAvailableTime);
      const diff = nextTime - now;

      if (diff <= 0) {
        setTimeRemaining(null);
        // Обновляем статус когда время истекло
        if (!ticketsRef.current.isLoading && !ticketsRef.current.error) {
          // Throttling: предотвращаем частые запросы при рассинхронизации времени клиента и севера
          // Если мы думаем, что время пришло, а сервер говорит "нет" (isAvailable=false),
          // мы будем повторять запрос не чаще чем раз в 5 секунд.
          const nowMs = Date.now();
          if (nowMs - lastFetchForTimerRef.current > 5000) {
            console.log('Timer expired, fetching ticket status...');
            lastFetchForTimerRef.current = nowMs;
            dispatch(fetchTicketStatus());
          }
        } else if (ticketsRef.current.error) {
          console.warn('Timer expired, but skipping fetch due to existing error:', ticketsRef.current.error);
        }
      } else {
        setTimeRemaining(diff);
      }
    };

    // Запускаем таймер
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [tickets.nextAvailableTime, tickets.isAvailable, dispatch]);

  // Форматировать время до следующего доступного тикета
  const formatTimeRemaining = useCallback(() => {
    if (!timeRemaining || timeRemaining <= 0) {
      return null;
    }

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

    return { hours, minutes, seconds };
  }, [timeRemaining]);

  // Получить форматированную строку времени
  const getTimeRemainingString = useCallback(() => {
    const time = formatTimeRemaining();
    if (!time) return null;
    return i18n.t('duration.h_m', '{{h}}h {{m}}m', { h: time.hours, m: time.minutes });
  }, [formatTimeRemaining]);

  // Забрать тикет
  const handleClaim = useCallback(async () => {
    try {
      const result = await dispatch(claimTicket()).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to claim ticket:', error);
      throw error;
    }
  }, [dispatch]);

  // Обновить статус вручную
  const refreshStatus = useCallback(() => {
    dispatch(fetchTicketStatus());
  }, [dispatch]);

  const clearTicketError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    ...tickets,
    timeRemaining,
    formatTimeRemaining,
    getTimeRemainingString,
    handleClaim,
    refreshStatus,
    clearError: clearTicketError,
  };
};

